import { useCallback, useEffect, useState } from "react";
import { PolicySaysChip, loadOperatingParameters } from "../components/PolicySaysChip";
import { useSearchParams } from "react-router-dom";
import { AlertTriangle, FileCheck2 } from "lucide-react";
import { theme, card, btnPrimary, type as text, pageHeading, pageSubtitle } from "../theme";
import { RulerProgress, StatusTag } from "../components/Drawing";
import { api, apiErrorMessage } from "../api/client";
import { useCurrentUser } from "../auth/useCurrentUser";
import { useFrameworks } from "../context/FrameworksContext";
import { iso27001Citation } from "../lib/standardCitation";
import { LoadingBlock } from "../components/Spinner";
import { PortalLink } from "../components/PortalLink";
import { RelatedRecords } from "../components/RelatedRecords";
import { NextSteps } from "../components/NextSteps";
import { DetailFields, DetailSection, EditButton, RecordActions } from "../components/RecordView";
import {
  ModalOverlay, ModalHeader, Field, Check, MultiPick, EmptyState, StatusPill, input, textarea, secondaryBtn, dangerBtn,
  fmtDay, fmtDateTime, toDateInput, dateInputToIso, fmtDateOnly, todayInput, relativeDue, type AttentionItem, type Option,
} from "./resilience/shared";

// Policy sign-off: management approves each adopted policy, staff confirm they have read it.
// Approving and acknowledging are done by the named person in their own account (My tasks);
// this page asks, distributes and tracks. Writes need documents:write.

type Standing = "none" | "pending" | "approved" | "review_due" | "rejected";

interface GovernedPolicy {
  templateId: string;
  title: string;
  adoptionState: string;
  evidenceDocumentId: string;
  fileName: string;
  documentVersion: number;
  adoptedAt: string | null;
  standing: Standing;
  approval: {
    id: string; status: string; approverUserId: string | null; approverName: string | null; requestedByName: string | null;
    selfApproved: boolean; decidedAt: string | null; nextReviewAt: string | null; decisionNote: string | null; requestNote: string | null; createdAt: string;
  } | null;
  campaigns: Array<{ id: string; title: string; status: string; dueAt: string; rate: number | null }>;
}

interface Progress { total: number; acknowledged: number; pending: number; excused: number; rate: number | null; overdue: boolean }

interface Campaign {
  id: string;
  displayId: number | null;
  title: string;
  message: string | null;
  documentTitle: string;
  documentVersion: number;
  evidenceDocumentId: string | null;
  autoEnrolJoiners: boolean;
  dueAt: string;
  status: "active" | "closed";
  closedAt: string | null;
  createdAt: string;
  audienceLabel: string;
  progress: Progress;
  policyTemplate?: { id: string; title: string } | null;
  approval?: { id: string; status: string; decidedAt: string | null; nextReviewAt: string | null } | null;
}

interface Acknowledgement {
  id: string;
  userId: string | null;
  memberName: string;
  status: "pending" | "acknowledged" | "excused";
  statement: string | null;
  documentVersion: number;
  openedAt: string | null;
  acknowledgedAt: string | null;
  excusedReason: string | null;
  lastRemindedAt: string | null;
  reminderCount: number;
  user: { id: string; displayName: string | null; email: string; isActive: boolean } | null;
}

interface Member { id: string; name: string; email: string }

interface Overview {
  policies: GovernedPolicy[];
  campaigns: Campaign[];
  attention: AttentionItem[];
  members: Member[];
  departments: Option[];
  sites: Option[];
  reviewIntervals: number[];
}

const th: React.CSSProperties = { padding: "10px 14px", borderBottom: "1px solid var(--c-line)", fontWeight: 600 };
const td: React.CSSProperties = { padding: "10px 14px", color: theme.color.muted, verticalAlign: "top" };
const headRow: React.CSSProperties = { textAlign: "left", color: theme.color.subtle, fontSize: text.label.fontSize };
const SEV_COLOR: Record<AttentionItem["severity"], string> = { high: "var(--c-danger)", medium: "var(--c-warning)", low: "var(--c-borderStrong)" };

function Pill({ tone, children, title }: { tone: "success" | "warning" | "danger" | "accent" | "muted"; children: React.ReactNode; title?: string }) {
  const s = {
    success: { bg: theme.color.successBg, color: theme.color.successText },
    warning: { bg: theme.color.warningBg, color: theme.color.warningText },
    danger:  { bg: theme.color.dangerBg,  color: theme.color.dangerText },
    accent:  { bg: theme.color.accentBg,  color: theme.color.accentText },
    muted:   { bg: theme.color.panelAlt,  color: theme.color.muted },
  }[tone];
  return <span title={title} style={{ fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 2, background: s.bg, color: s.color, whiteSpace: "nowrap" }}>{children}</span>;
}

function Tile({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div style={{ ...card, padding: "14px 16px", height: "100%" }}>
      <div style={text.statLabel}>{label}</div>
      <div style={{ ...text.stat, color: theme.color.heading, marginTop: 4 }}>{value}</div>
      {sub && <div style={{ ...text.hint, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function ProgressBar({ rate }: { rate: number | null }) {
  return (
    <RulerProgress value={rate ?? 0} fill={(rate ?? 0) >= 100 ? "var(--c-success)" : undefined} label="Acknowledged" height={10} style={{ minWidth: 80 }} />
  );
}

function StandingPill({ p }: { p: GovernedPolicy }) {
  const a = p.approval;
  switch (p.standing) {
    case "pending":
      return <StatusTag tone="partial">Awaiting {a?.approverName ?? "approver"}</StatusTag>;
    case "approved":
      return <span style={{ display: "inline-flex", flexDirection: "column", gap: 2 }}>
        <StatusTag tone="met">Approved {fmtDay(a?.decidedAt)}</StatusTag>
        {a?.selfApproved && <span style={text.meta}>self-approved (sole member)</span>}
      </span>;
    case "review_due":
      return <Pill tone="danger">Review overdue</Pill>;
    case "rejected":
      return <span style={{ display: "inline-flex", flexDirection: "column", gap: 2, maxWidth: 240 }}>
        <StatusTag tone="gap" title={a?.decisionNote ?? undefined}>Sent back</StatusTag>
        {a?.decisionNote && <span style={text.meta}>{a.decisionNote}</span>}
      </span>;
    default:
      return <StatusTag tone="none">Not approved</StatusTag>;
  }
}

export function PolicyGovernancePage() {
  const { enrolled } = useFrameworks();
  const { user } = useCurrentUser();
  const canEdit = !!user?.permissions?.includes("documents:write");
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState<Overview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [approvalFor, setApprovalFor] = useState<GovernedPolicy | null>(null);
  const [campaignFor, setCampaignFor] = useState<GovernedPolicy | null>(null);
  const openCampaignId = params.get("campaign");
  // ?policy=<evidenceDocumentId> opens a policy's read-only sign-off view.
  const openPolicyId = params.get("policy");

  const load = useCallback(async () => {
    try {
      setData(await api.get<Overview>("/policy-governance"));
      setError(null);
    } catch (e) {
      setError(apiErrorMessage(e, "Could not load policy sign-off"));
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  function openCampaign(id: string | null) {
    const next = new URLSearchParams(params);
    next.delete("policy");
    if (id) next.set("campaign", id); else next.delete("campaign");
    setParams(next, { replace: true });
  }
  function openPolicy(id: string | null) {
    const next = new URLSearchParams(params);
    if (id) next.set("policy", id); else next.delete("policy");
    setParams(next, { replace: true });
  }

  async function withdraw(p: GovernedPolicy) {
    if (!p.approval || !window.confirm(`Withdraw the approval request for "${p.title}"?`)) return;
    try {
      await api.post(`/policy-governance/approvals/${p.approval.id}/withdraw`);
      setNotice(null); await load();
    } catch (e) { setError(apiErrorMessage(e, "Could not withdraw the request")); }
  }

  if (!data && !error) return <div className="page-pad"><LoadingBlock /></div>;

  const policies = data?.policies ?? [];
  const campaigns = data?.campaigns ?? [];
  const approved = policies.filter((p) => p.standing === "approved").length;
  const awaiting = policies.filter((p) => p.standing === "pending").length;
  const reviewDue = policies.filter((p) => p.standing === "review_due").length;
  const active = campaigns.filter((c) => c.status === "active");
  const ackDone = active.reduce((n, c) => n + c.progress.acknowledged, 0);
  const ackExpected = active.reduce((n, c) => n + c.progress.total - c.progress.excused, 0);
  const ackRate = ackExpected ? Math.round((ackDone / ackExpected) * 100) : null;
  const attention = data?.attention ?? [];

  return (
    <div className="page-pad">
      <h1 style={{ ...pageHeading, display: "flex", alignItems: "center", gap: 8 }}>
        <FileCheck2 size={20} /> Policy sign-off
      </h1>
      <p style={{ ...pageSubtitle, maxWidth: 760 }}>
        Management approves each adopted policy, then staff confirm they have read it. Auditors ask for both{iso27001Citation(enrolled, "5.2, A.5.1, 7.3")}.
      </p>
      <PolicySaysChip keys={["policy.review_months"]} />
      <p style={{ margin: "6px 0 20px", ...text.hint, maxWidth: 760 }}>
        Approval must come from someone other than the person who asks for it. Approvers decide, and staff acknowledge, in their own account from My tasks.
      </p>

      {error && <div style={{ ...card, padding: 12, marginBottom: 16, ...text.body, color: theme.color.dangerText }}>{error}</div>}
      {notice && <div style={{ ...card, padding: 12, marginBottom: 16, color: theme.color.successText, fontSize: text.body.fontSize }}>{notice}</div>}

      {data && (
        <div style={{ display: "grid", gap: 20 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
            <Tile label="Adopted policies" value={policies.length} sub="reviewed copies on file" />
            <Tile label="Approved" value={approved} sub="and in date for review" />
            <Tile label="Awaiting approval" value={awaiting} />
            <Tile label="Review overdue" value={reviewDue} sub="approval past its review date" />
            <Tile label="Acknowledged" value={ackRate != null ? `${ackRate}%` : "—"} sub={active.length ? `${ackDone} of ${ackExpected} across ${active.length} active campaign${active.length === 1 ? "" : "s"}` : "no active campaigns"} />
          </div>

          <div style={{ ...card, padding: 0 }}>
            <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--c-border)", display: "flex", alignItems: "center", gap: 8, ...text.sectionTitle, margin: 0 }}>
              <AlertTriangle size={15} /> Needs attention
              <span style={{ ...text.hint, fontWeight: 400 }}>{attention.length} item{attention.length === 1 ? "" : "s"}</span>
            </div>
            {attention.length === 0 ? (
              <div style={{ padding: 20, ...text.body, color: theme.color.muted }}>Nothing outstanding.</div>
            ) : (
              <div>
                {[...attention].sort((a, b) => sevRank(a.severity) - sevRank(b.severity)).map((a) => (
                  <PortalLink key={a.id} to={a.href} style={{ textDecoration: "none" }}>
                    <div style={{ display: "flex", gap: 12, padding: "11px 18px", borderBottom: "1px solid var(--c-border)", alignItems: "flex-start" }}>
                      <span style={{ width: 8, height: 8, borderRadius: 999, marginTop: 6, background: SEV_COLOR[a.severity], flexShrink: 0 }} />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={text.itemTitle}>{a.title}</div>
                        <div style={{ ...text.hint, marginTop: 2 }}>{a.description}</div>
                      </div>
                      {a.dueAt && <span style={{ fontSize: text.meta.fontSize, color: a.severity === "high" ? theme.color.dangerText : theme.color.muted, whiteSpace: "nowrap" }}>{relativeDue(a.dueAt)}</span>}
                    </div>
                  </PortalLink>
                ))}
              </div>
            )}
          </div>

          <section>
            <h2 style={{ ...text.sectionTitle, margin: "0 0 10px" }}>Policies</h2>
            {policies.length === 0 ? (
              <div style={card}>
                <EmptyState
                  title="No adopted policies yet"
                  detail="A policy can be approved and sent to staff once it has been adopted: a reviewed copy of it uploaded from Write policies, or edited in the Policies folder of your connected storage. The platform's own drafts are never sent for sign-off."
                  action={<PortalLink to="/templates" style={{ ...btnPrimary, textDecoration: "none", display: "inline-block" }}>Go to Write policies</PortalLink>}
                />
              </div>
            ) : (
              <div style={{ ...card, padding: 0, overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.body.fontSize }}>
                  <thead><tr style={headRow}>
                    {["Policy", "Version", "Approval", "Next review", "Distributed"].map((h, i) => <th key={i} style={th}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {policies.map((p) => {
                      const latest = p.campaigns[0];
                      return (
                        <tr key={p.evidenceDocumentId} onClick={() => openPolicy(p.evidenceDocumentId)} style={{ borderBottom: "1px solid var(--c-border)", cursor: "pointer" }}>
                          <td style={{ ...td, color: theme.color.text, fontWeight: 600 }}>
                            {p.title}
                            <div style={{ ...text.meta, fontWeight: 400, overflowWrap: "anywhere" }}>{p.fileName}</div>
                          </td>
                          <td style={td}>v{p.documentVersion}</td>
                          <td style={td}><StandingPill p={p} /></td>
                          <td style={{ ...td, whiteSpace: "nowrap", color: p.standing === "review_due" ? theme.color.dangerText : theme.color.muted }}>
                            {p.approval?.nextReviewAt && (p.standing === "approved" || p.standing === "review_due") ? fmtDay(p.approval.nextReviewAt) : "—"}
                          </td>
                          <td style={td}>
                            {latest ? (
                              <button type="button" onClick={(e) => { e.stopPropagation(); openCampaign(latest.id); }} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: "var(--c-accentText)", fontSize: text.body.fontSize, textAlign: "left" }}>
                                {latest.rate != null ? `${latest.rate}% acknowledged` : "Sent"}
                                <div style={text.meta}>{latest.status === "closed" ? "closed" : `due ${fmtDateOnly(latest.dueAt)}`}</div>
                              </button>
                            ) : "Not sent"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section>
            <h2 style={{ ...text.sectionTitle, margin: "0 0 10px" }}>Campaigns</h2>
            {campaigns.length === 0 ? (
              <div style={card}><EmptyState title="Nothing sent to staff yet" detail="Once a policy is approved, send it to everyone or to chosen departments, sites or people. Each person reads it and confirms in their own account, and you see who has and has not." /></div>
            ) : (
              <div style={{ ...card, padding: 0, overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.body.fontSize }}>
                  <thead><tr style={headRow}>
                    {["Campaign", "Policy", "Sent to", "Due", "Progress", "Status"].map((h) => <th key={h} style={th}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {campaigns.map((c) => (
                      <tr key={c.id} onClick={() => openCampaign(c.id)} style={{ borderBottom: "1px solid var(--c-border)", cursor: "pointer" }}>
                        <td style={{ ...td, color: theme.color.text, fontWeight: 600 }}>{c.title}</td>
                        <td style={td}>{c.documentTitle} v{c.documentVersion}</td>
                        <td style={td}>{c.audienceLabel}</td>
                        <td style={{ ...td, whiteSpace: "nowrap", color: c.progress.overdue && c.status === "active" ? theme.color.dangerText : theme.color.muted }}>{fmtDateOnly(c.dueAt)}</td>
                        <td style={{ ...td, minWidth: 140 }}>
                          <ProgressBar rate={c.progress.rate} />
                          <div style={{ fontSize: text.meta.fontSize, marginTop: 4 }}>{c.progress.acknowledged} of {c.progress.total - c.progress.excused}{c.progress.rate != null ? ` · ${c.progress.rate}%` : ""}</div>
                        </td>
                        <td style={td}>{c.status === "closed" ? <StatusPill value="closed" /> : c.progress.overdue ? <StatusPill value="overdue" /> : <StatusPill value="active" />}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      )}

      {openPolicyId && data && !approvalFor && !campaignFor && (() => {
        const p = policies.find((x) => x.evidenceDocumentId === openPolicyId);
        return p ? (
          <PolicyView policy={p} canEdit={canEdit} onClose={() => openPolicy(null)}
            onAsk={() => setApprovalFor(p)} onWithdraw={() => void withdraw(p)} onSend={() => setCampaignFor(p)}
            onOpenCampaign={(id) => openCampaign(id)} />
        ) : null;
      })()}
      {approvalFor && data && (
        <ApprovalModal policy={approvalFor} members={data.members} intervals={data.reviewIntervals} currentUserId={user?.id ?? null}
          onClose={() => setApprovalFor(null)}
          onDone={async (msg) => { setApprovalFor(null); setNotice(msg); await load(); }} />
      )}
      {campaignFor && data && (
        <CampaignModal policy={campaignFor} data={data}
          onClose={() => setCampaignFor(null)}
          onDone={async (msg) => { setCampaignFor(null); setNotice(msg); await load(); }} />
      )}
      {openCampaignId && (
        <CampaignDetail id={openCampaignId} canEdit={canEdit} onClose={() => openCampaign(null)} onChanged={load} />
      )}
    </div>
  );
}

const sevRank = (s: AttentionItem["severity"]) => (s === "high" ? 0 : s === "medium" ? 1 : 2);

// ── Policy view ────────────────────────────────────────────────────────────

function PolicyView({ policy: p, canEdit, onClose, onAsk, onWithdraw, onSend, onOpenCampaign }: {
  policy: GovernedPolicy; canEdit: boolean; onClose: () => void; onAsk: () => void; onWithdraw: () => void; onSend: () => void; onOpenCampaign: (id: string) => void;
}) {
  const a = p.approval;
  const canAsk = p.standing === "none" || p.standing === "rejected" || p.standing === "review_due";
  const canWithdraw = p.standing === "pending";
  const canSend = p.standing === "approved" || p.standing === "review_due";
  return (
    <ModalOverlay onClose={onClose} wide>
      <ModalHeader title={`${p.title} v${p.documentVersion}`} onClose={onClose} />
      <div style={{ padding: "8px 20px 20px" }}>
        <NextSteps items={[
          // ISO 27001 5.2 / A.5.1: approved by management, communicated, acknowledged and reviewed.
          { done: p.standing !== "none" && p.standing !== "rejected", label: p.standing === "rejected" ? "Revise it and ask for approval again" : "Ask management to approve it", urgent: p.standing === "rejected" },
          { done: p.standing === "approved" || p.standing === "review_due", label: "Have it approved", hint: p.standing === "pending" ? `Waiting on ${a?.approverName ?? "the approver"}.` : undefined },
          { done: p.campaigns.length > 0, label: "Send it to staff to read and acknowledge" },
          ...(p.standing === "review_due" ? [{ done: false, label: "Review it (the review date has passed)", urgent: true }] : []),
        ]} />
        <DetailSection title="Policy">
          <DetailFields fields={[
            { label: "File", value: p.fileName },
            { label: "Version", value: `v${p.documentVersion}` },
            { label: "Adopted", value: p.adoptedAt ? fmtDay(p.adoptedAt) : null },
          ]} />
        </DetailSection>
        <DetailSection title="Approval" aside={<StandingPill p={p} />}>
          {a ? (
            <DetailFields fields={[
              { label: "Approver", value: a.approverName },
              { label: "Requested by", value: a.requestedByName },
              { label: "Requested", value: fmtDay(a.createdAt) },
              { label: "Decided", value: a.decidedAt ? fmtDay(a.decidedAt) : null },
              { label: "Next review", value: a.nextReviewAt && (p.standing === "approved" || p.standing === "review_due") ? fmtDay(a.nextReviewAt) : null },
              { label: "Self-approved", value: a.selfApproved ? "Yes (sole member)" : null },
              { label: "Note to the approver", value: a.requestNote, wide: true, multiline: true },
              { label: "Approver's note", value: a.decisionNote, wide: true, multiline: true },
            ]} />
          ) : <div style={{ ...text.body, color: theme.color.muted }}>Not yet sent for approval.</div>}
        </DetailSection>
        <DetailSection title="Sent to staff">
          {p.campaigns.length === 0 ? <div style={{ ...text.body, color: theme.color.muted }}>Not sent to staff yet.</div> : (
            <div style={{ display: "grid" }}>
              {p.campaigns.map((c) => (
                <div key={c.id} role="button" onClick={() => onOpenCampaign(c.id)} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "8px 0", borderTop: "1px solid var(--c-border)", cursor: "pointer", fontSize: text.body.fontSize }}>
                  <span style={{ color: "var(--c-accentText)", minWidth: 0 }}>{c.title}</span>
                  <span style={{ ...text.hint, whiteSpace: "nowrap" }}>{c.rate != null ? `${c.rate}% acknowledged` : "Sent"} · {c.status === "closed" ? "closed" : `due ${fmtDateOnly(c.dueAt)}`}</span>
                </div>
              ))}
            </div>
          )}
        </DetailSection>
        {/* The document on file, the template it was written from and the review on the calendar. */}
        <RelatedRecords
          entity={{ kind: "document", id: p.evidenceDocumentId }}
          defaultOpen={false}
          links={[
            { kindLabel: "Evidence", label: `${p.fileName} (v${p.documentVersion})`, href: `/documents?document=${encodeURIComponent(p.evidenceDocumentId)}` },
            { kindLabel: "Policy template", label: p.title, href: "/templates" },
            ...(a?.nextReviewAt && (p.standing === "approved" || p.standing === "review_due") ? [{ kindLabel: "Calendar", label: `Policy review due ${fmtDay(a.nextReviewAt)}`, href: "/schedule" }] : []),
          ]}
        />
        {canEdit && (canAsk || canWithdraw || canSend) && (
          <RecordActions>
            {canAsk && <button type="button" style={secondaryBtn} onClick={onAsk}>Ask for approval</button>}
            {canWithdraw && <button type="button" style={dangerBtn} onClick={onWithdraw}>Withdraw request</button>}
            {canSend && <button type="button" style={{ ...btnPrimary, padding: "6px 12px", fontSize: text.hint.fontSize }} onClick={onSend}>Send to staff</button>}
          </RecordActions>
        )}
      </div>
    </ModalOverlay>
  );
}

// ── Ask for approval ───────────────────────────────────────────────────────

function ApprovalModal({ policy, members, intervals, currentUserId, onClose, onDone }: {
  policy: GovernedPolicy; members: Member[]; intervals: number[]; currentUserId: string | null; onClose: () => void; onDone: (msg: string) => Promise<void>;
}) {
  const [approverUserId, setApprover] = useState("");
  const [note, setNote] = useState("");
  const [months, setMonths] = useState(intervals.includes(12) ? 12 : intervals[0] ?? 12);
  // Start from the organisation's own policy review interval (Settings -> Operating parameters).
  const intervalKey = intervals.join(",");
  useEffect(() => {
    let live = true;
    loadOperatingParameters()
      .then((r) => {
        const v = r.parameters.find((p) => p.key === "policy.review_months")?.value;
        if (live && v && intervalKey.split(",").map(Number).includes(v)) setMonths(v);
      })
      .catch(() => { /* keep the default */ });
    return () => { live = false; };
  }, [intervalKey]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault(); setSaving(true); setError(null);
    try {
      await api.post("/policy-governance/approvals", { evidenceDocumentId: policy.evidenceDocumentId, approverUserId, requestNote: note.trim() || null, reviewIntervalMonths: months });
      const who = members.find((m) => m.id === approverUserId)?.name ?? "The approver";
      await onDone(`${who} has been asked to approve ${policy.title}.`);
    } catch (err) { setError(apiErrorMessage(err, "Could not ask for approval")); setSaving(false); }
  }

  return (
    <ModalOverlay onClose={onClose}>
      <ModalHeader title={`Ask for approval: ${policy.title} v${policy.documentVersion}`} onClose={onClose} />
      <form onSubmit={submit} style={{ padding: 20, display: "grid", gap: 14 }}>
        <div style={{ ...text.body, color: theme.color.muted, lineHeight: 1.5 }}>
          The approver is emailed and decides from My tasks. They see exactly this version. Choose someone other than yourself; you can approve your own request only when you are the organisation's sole member.
        </div>
        {policy.standing === "rejected" && policy.approval?.decisionNote && (
          <div style={{ fontSize: text.hint.fontSize, color: theme.color.warningText, background: theme.color.warningBg, padding: "8px 12px", borderRadius: 3 }}>
            Last sent back by {policy.approval.approverName ?? "the approver"}: {policy.approval.decisionNote}
          </div>
        )}
        <Field label="Approver *">
          <select required value={approverUserId} onChange={(e) => setApprover(e.target.value)} style={input}>
            <option value="">Choose a member</option>
            {members.map((m) => <option key={m.id} value={m.id}>{m.name}{m.id === currentUserId ? " (you)" : ""} · {m.email}</option>)}
          </select>
        </Field>
        <Field label="Note to the approver"><textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} style={textarea} placeholder="What changed, or what to look at" /></Field>
        <Field label="Review it again after" help="The approval sets the next review date. Past it, the policy shows as review overdue.">
          <select value={months} onChange={(e) => setMonths(Number(e.target.value))} style={input}>
            {intervals.map((n) => <option key={n} value={n}>{n} months</option>)}
          </select>
        </Field>
        {error && <div style={{ ...text.body, color: theme.color.dangerText }}>{error}</div>}
        <div style={{ display: "flex", gap: 10 }}>
          <button type="submit" disabled={saving || !approverUserId} style={{ ...btnPrimary, opacity: saving || !approverUserId ? 0.6 : 1 }}>{saving ? "Sending…" : "Ask for approval"}</button>
          <button type="button" style={secondaryBtn} onClick={onClose}>Cancel</button>
        </div>
      </form>
    </ModalOverlay>
  );
}

// ── Send to staff ──────────────────────────────────────────────────────────

interface AudienceValue { all: boolean; departmentIds: string[]; siteIds: string[]; userIds: string[] }

function AudiencePicker({ value, onChange, departments, sites, members }: { value: AudienceValue; onChange: (v: AudienceValue) => void; departments: Option[]; sites: Option[]; members: Option[] }) {
  return (
    <div style={{ display: "grid", gap: 10 }}>
      <Check checked={value.all} onChange={(all) => onChange({ ...value, all })} label="Everyone" help="Every active member of the organisation." />
      {!value.all && (
        <>
          <Field label="Departments"><MultiPick options={departments} selected={value.departmentIds} onChange={(departmentIds) => onChange({ ...value, departmentIds })} empty="No departments set up" /></Field>
          <Field label="Sites"><MultiPick options={sites} selected={value.siteIds} onChange={(siteIds) => onChange({ ...value, siteIds })} empty="No sites set up" /></Field>
          <Field label="People"><MultiPick options={members} selected={value.userIds} onChange={(userIds) => onChange({ ...value, userIds })} empty="No active members" /></Field>
        </>
      )}
    </div>
  );
}

const audienceEmpty = (a: AudienceValue) => !a.all && a.departmentIds.length + a.siteIds.length + a.userIds.length === 0;
const plusDays = (n: number) => todayInput(n);

function CampaignModal({ policy, data, onClose, onDone }: { policy: GovernedPolicy; data: Overview; onClose: () => void; onDone: (msg: string) => Promise<void> }) {
  const [title, setTitle] = useState(`Acknowledge ${policy.title}`);
  const [message, setMessage] = useState("");
  const [due, setDue] = useState(plusDays(14));
  const [audience, setAudience] = useState<AudienceValue>({ all: true, departmentIds: [], siteIds: [], userIds: [] });
  const [autoEnrol, setAutoEnrol] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const memberOptions: Option[] = data.members.map((m) => ({ id: m.id, name: m.name }));

  async function submit(e: React.FormEvent) {
    e.preventDefault(); setSaving(true); setError(null);
    try {
      // End of the chosen day, so "due 3 Oct" means the whole of 3 October (lib/dateOnly.ts).
      const dueAt = dateInputToIso(due, { endOfDay: true });
      const res = await api.post<{ enrolled: number }>("/policy-governance/campaigns", {
        evidenceDocumentId: policy.evidenceDocumentId, title: title.trim() || undefined, message: message.trim() || null,
        audience, dueAt, autoEnrolJoiners: autoEnrol,
      });
      await onDone(`Sent to ${res.enrolled} ${res.enrolled === 1 ? "person" : "people"}.`);
    } catch (err) { setError(apiErrorMessage(err, "Could not send the policy")); setSaving(false); }
  }

  return (
    <ModalOverlay onClose={onClose}>
      <ModalHeader title={`Send to staff: ${policy.title} v${policy.documentVersion}`} onClose={onClose} />
      <form onSubmit={submit} style={{ padding: 20, display: "grid", gap: 14 }}>
        <div style={{ ...text.body, color: theme.color.muted, lineHeight: 1.5 }}>
          Each person is emailed, opens the approved copy from My tasks and confirms they have read it and will follow it. Reminders go out automatically as the due date nears.
        </div>
        <Field label="Title"><input value={title} onChange={(e) => setTitle(e.target.value)} style={input} maxLength={200} /></Field>
        <Field label="Message" help="Shown with the task and in the email."><textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} style={textarea} /></Field>
        <Field label="Due *"><input type="date" required value={due} min={plusDays(0)} onChange={(e) => setDue(e.target.value)} style={input} /></Field>
        <Field label="Send to *">
          <AudiencePicker value={audience} onChange={setAudience} departments={data.departments} sites={data.sites} members={memberOptions} />
        </Field>
        <Check checked={autoEnrol} onChange={setAutoEnrol} label="Add people who join later" help="New members who match this audience get the task when they join." />
        {error && <div style={{ ...text.body, color: theme.color.dangerText }}>{error}</div>}
        <div style={{ display: "flex", gap: 10 }}>
          <button type="submit" disabled={saving || audienceEmpty(audience) || !due} style={{ ...btnPrimary, opacity: saving || audienceEmpty(audience) || !due ? 0.6 : 1 }}>{saving ? "Sending…" : "Send"}</button>
          <button type="button" style={secondaryBtn} onClick={onClose}>Cancel</button>
        </div>
      </form>
    </ModalOverlay>
  );
}

// ── Campaign detail ────────────────────────────────────────────────────────

type CampaignWithAcks = Campaign & { acknowledgements: Acknowledgement[] };

function CampaignDetail({ id, canEdit, onClose, onChanged }: { id: string; canEdit: boolean; onClose: () => void; onChanged: () => Promise<void> }) {
  const [campaign, setCampaign] = useState<CampaignWithAcks | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [due, setDue] = useState("");
  const [excuseId, setExcuseId] = useState("");
  // The campaign's own wording and whether joiners are added. The policy version and the
  // audience stay as sent: they are what people acknowledged.
  const [edit, setEdit] = useState<{ title: string; message: string; autoEnrolJoiners: boolean } | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get<{ campaign: CampaignWithAcks }>(`/policy-governance/campaigns/${id}`);
      setCampaign(res.campaign); setDue(toDateInput(res.campaign.dueAt)); setError(null);
    } catch (e) { setError(apiErrorMessage(e, "Could not load the campaign")); }
  }, [id]);
  useEffect(() => { void load(); }, [load]);

  async function act(fn: () => Promise<string | null>) {
    setBusy(true); setError(null); setNotice(null);
    try {
      const msg = await fn();
      if (msg) setNotice(msg);
      await load(); await onChanged();
    } catch (e) { setError(apiErrorMessage(e, "Could not update the campaign")); }
    setBusy(false);
  }

  const remind = () => act(async () => {
    const r = await api.post<{ reminded: number }>(`/policy-governance/campaigns/${id}/remind`);
    return r.reminded ? `Reminded ${r.reminded} ${r.reminded === 1 ? "person" : "people"}.` : "Nobody to remind: everyone outstanding was reminded in the last day.";
  });
  const setStatus = (status: "active" | "closed") => act(async () => { await api.patch(`/policy-governance/campaigns/${id}`, { status }); return status === "closed" ? "Campaign closed." : "Campaign reopened."; });
  const saveDue = () => act(async () => { await api.patch(`/policy-governance/campaigns/${id}`, { dueAt: dateInputToIso(due, { endOfDay: true }) }); return "Due date changed."; });
  const saveEdit = () => {
    if (!edit) return;
    void act(async () => {
      await api.patch(`/policy-governance/campaigns/${id}`, { title: edit.title.trim(), message: edit.message.trim() || null, autoEnrolJoiners: edit.autoEnrolJoiners });
      setEdit(null);
      return "Campaign saved.";
    });
  };
  const excuse = (a: Acknowledgement) => {
    const reason = window.prompt(`Why does ${nameOf(a)} not need to acknowledge this? (for example, long-term leave)`);
    if (reason == null) return;
    if (reason.trim().length < 3) { setError("Give a reason of at least three characters."); return; }
    void act(async () => { await api.post(`/policy-governance/acknowledgements/${a.id}/excuse`, { reason: reason.trim() }); return `${nameOf(a)} excused.`; });
  };

  const c = campaign;
  const acks = c ? [...c.acknowledgements].sort((a, b) => ackRank(a.status) - ackRank(b.status)) : [];

  return (
    <ModalOverlay onClose={onClose} wide>
      <ModalHeader title={c ? c.title : "Campaign"} onClose={onClose}
        right={canEdit && c && !edit ? <EditButton onClick={() => setEdit({ title: c.title, message: c.message ?? "", autoEnrolJoiners: c.autoEnrolJoiners })} /> : undefined} />
      <div style={{ padding: "8px 20px 20px", display: "grid", gap: 4 }}>
        {!c && !error && <LoadingBlock />}
        {error && <div style={{ ...text.body, color: theme.color.dangerText }}>{error}</div>}
        {notice && <div style={{ color: theme.color.successText, fontSize: text.body.fontSize }}>{notice}</div>}
        {c && edit && (
          <div style={{ display: "grid", gap: 12, padding: "8px 0" }}>
            <Field label="Title *"><input required value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} style={input} maxLength={200} /></Field>
            <Field label="Message" help="Shown to each person with the policy."><textarea rows={3} value={edit.message} onChange={(e) => setEdit({ ...edit, message: e.target.value })} style={textarea} maxLength={2000} /></Field>
            <Check checked={edit.autoEnrolJoiners} onChange={(v) => setEdit({ ...edit, autoEnrolJoiners: v })} label="Add people who join later" help="New members who match the audience are asked to acknowledge it when they join." />
            <div style={{ display: "flex", gap: 10 }}>
              <button type="button" disabled={busy || !edit.title.trim()} style={{ ...btnPrimary, opacity: busy || !edit.title.trim() ? 0.6 : 1 }} onClick={saveEdit}>{busy ? "Saving…" : "Save changes"}</button>
              <button type="button" style={secondaryBtn} onClick={() => setEdit(null)}>Cancel</button>
            </div>
          </div>
        )}
        {c && !edit && (
          <>
            <DetailSection title="Campaign" aside={c.status === "closed" ? <StatusPill value="closed" /> : c.progress.overdue ? <StatusPill value="overdue" /> : <StatusPill value="active" />}>
              <DetailFields fields={[
                { label: "Policy", value: `${c.documentTitle} v${c.documentVersion}` },
                { label: "Sent to", value: c.audienceLabel },
                { label: "Sent", value: fmtDay(c.createdAt) },
                { label: "Due", value: fmtDateOnly(c.dueAt) },
                { label: "People who join later", value: c.autoEnrolJoiners ? "Added automatically" : "Not added" },
                { label: "Closed", value: c.closedAt ? fmtDay(c.closedAt) : null },
                { label: "Message", value: c.message, wide: true, multiline: true },
              ]} />
            </DetailSection>
            <DetailSection title="Progress">
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", ...text.body, color: theme.color.muted }}>
                <div style={{ flex: "1 1 200px", maxWidth: 320 }}><ProgressBar rate={c.progress.rate} /></div>
                <span>{c.progress.acknowledged} acknowledged · {c.progress.pending} outstanding · {c.progress.excused} excused{c.progress.rate != null ? ` · ${c.progress.rate}%` : ""}</span>
              </div>
            </DetailSection>

            <RelatedRecords
              entity={c.evidenceDocumentId ? { kind: "document", id: c.evidenceDocumentId } : null}
              defaultOpen={false}
              links={[
                ...(c.evidenceDocumentId ? [{ kindLabel: "Evidence", label: `${c.documentTitle} (v${c.documentVersion})`, href: `/documents?document=${encodeURIComponent(c.evidenceDocumentId)}` }] : []),
                ...(c.policyTemplate ? [{ kindLabel: "Policy template", label: c.policyTemplate.title, href: "/templates" }] : []),
                ...(c.approval ? [{ kindLabel: "Approval", label: `${c.approval.status}${c.approval.decidedAt ? ` ${fmtDay(c.approval.decidedAt)}` : ""}${c.approval.nextReviewAt ? `, next review ${fmtDay(c.approval.nextReviewAt)}` : ""}`, href: null }] : []),
              ]}
            />

            <DetailSection title="People">
            <div style={{ border: "1px solid var(--c-border)", borderRadius: 3, overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.body.fontSize }}>
                <thead><tr style={headRow}>{["Person", "Status", "When"].map((h, i) => <th key={i} style={th}>{h}</th>)}</tr></thead>
                <tbody>
                  {acks.map((a) => (
                    <tr key={a.id} style={{ borderBottom: "1px solid var(--c-border)" }}>
                      <td style={{ ...td, color: theme.color.text, fontWeight: 600 }}>
                        {nameOf(a)}
                        {!a.user && <span style={{ fontWeight: 400, color: theme.color.faint }}> (no longer a member)</span>}
                        {a.user && a.user.displayName && <div style={{ ...text.meta, fontWeight: 400 }}>{a.user.email}</div>}
                      </td>
                      <td style={td}>
                        {a.status === "acknowledged" ? <StatusPill value="acknowledged" /> : a.status === "excused" ? <Pill tone="muted" title={a.excusedReason ?? undefined}>Excused</Pill> : <StatusPill value="pending" />}
                        {a.status === "excused" && a.excusedReason && <div style={{ ...text.meta, marginTop: 3 }}>{a.excusedReason}</div>}
                      </td>
                      <td style={{ ...td, fontSize: text.hint.fontSize }}>
                        {a.acknowledgedAt ? fmtDateTime(a.acknowledgedAt) : a.openedAt ? <span>opened {fmtDateTime(a.openedAt)}</span> : a.status === "pending" ? "not opened" : "—"}
                        {a.status === "pending" && a.reminderCount > 0 && <div style={text.meta}>reminded {a.reminderCount}×</div>}
                      </td>
                    </tr>
                  ))}
                  {acks.length === 0 && <tr><td colSpan={3} style={{ ...td, padding: 20 }}>Nobody is on this campaign.</td></tr>}
                </tbody>
              </table>
            </div>
            </DetailSection>

            {canEdit && (() => {
              const pending = acks.filter((a) => a.status === "pending");
              return (
                <RecordActions>
                  {c.status === "active" && c.progress.pending > 0 && <button type="button" disabled={busy} style={secondaryBtn} onClick={remind}>Remind everyone outstanding</button>}
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, ...text.hint }}>
                    Due
                    <input type="date" value={due} min={plusDays(0)} onChange={(e) => setDue(e.target.value)} style={{ ...input, width: "auto" }} aria-label="Due date" />
                    <button type="button" disabled={busy || !due || due === toDateInput(c.dueAt)} style={secondaryBtn} onClick={saveDue}>Change due date</button>
                  </span>
                  {pending.length > 0 && (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                      <select value={excuseId} onChange={(e) => setExcuseId(e.target.value)} style={{ ...input, width: "auto", maxWidth: 240 }} aria-label="Person to excuse">
                        <option value="">Choose someone to excuse…</option>
                        {pending.map((a) => <option key={a.id} value={a.id}>{nameOf(a)}</option>)}
                      </select>
                      <button type="button" disabled={busy || !excuseId} style={{ ...secondaryBtn, opacity: excuseId ? 1 : 0.6 }} onClick={() => {
                        const a = pending.find((x) => x.id === excuseId);
                        if (a) { excuse(a); setExcuseId(""); }
                      }}>Excuse</button>
                    </span>
                  )}
                  {c.status === "active"
                    ? <button type="button" disabled={busy} style={dangerBtn} onClick={() => { if (window.confirm("Close this campaign? Outstanding people can no longer acknowledge it.")) void setStatus("closed"); }}>Close campaign</button>
                    : <button type="button" disabled={busy} style={secondaryBtn} onClick={() => setStatus("active")}>Reopen</button>}
                </RecordActions>
              );
            })()}
          </>
        )}
      </div>
    </ModalOverlay>
  );
}

const nameOf = (a: Acknowledgement) => a.user ? a.user.displayName || a.user.email : a.memberName || "Former member";
const ackRank = (s: Acknowledgement["status"]) => (s === "pending" ? 0 : s === "acknowledged" ? 1 : 2);
